// Fetch every ProductVehicleFitment record across all pages.
async function fetchAllProductVehicleFitments(api, query, pageSize = 250) {
    const model = api?.ProductVehicleFitment;
    if (!model) return [];

    const allRecords = [];
    let afterCursor = null;
    let lastSeenId = null;
    let forceIdGreaterThanPaging = false;
    let keepGoing = true;
    let guard = 0;
    const originalSelect = query?.select || {};
    const selectWithId = { ...originalSelect, id: true };

    while (keepGoing && guard < 200) {
        guard += 1;
        const pageQuery = { ...query, select: selectWithId, first: pageSize };
        if (forceIdGreaterThanPaging && lastSeenId) {
            const baseFilter = query?.filter || {};
            pageQuery.filter = {
                AND: [
                    baseFilter,
                    { id: { greaterThan: lastSeenId } }
                ]
            };
            delete pageQuery.after;
        } else if (afterCursor) {
            pageQuery.after = afterCursor;
        }

        const response = await model.findMany(pageQuery);

        const records = Array.isArray(response)
            ? response
            : (response?.data || response?.items || []);

        allRecords.push(...records);

        const pageInfo = response?.pageInfo || response?.pagination || null;
        const hasNextPage = Boolean(pageInfo?.hasNextPage);
        const endCursor = pageInfo?.endCursor || response?.endCursor || response?.nextCursor || null;
        const fallbackAfterFromId = records.length > 0 ? records[records.length - 1]?.id : null;
        const canFallbackPaginate = !pageInfo && records.length === pageSize && fallbackAfterFromId;
        const cursorAdvanced = Boolean(endCursor && endCursor !== afterCursor);
        const canContinueWithCursorDespiteFlag = Boolean(pageInfo && records.length === pageSize && cursorAdvanced);
        const canSwitchToIdRangePaging = Boolean(records.length === pageSize && fallbackAfterFromId && (!cursorAdvanced || !hasNextPage));

        if (fallbackAfterFromId) {
            lastSeenId = fallbackAfterFromId;
        }

        if (hasNextPage && endCursor) {
            afterCursor = endCursor;
            forceIdGreaterThanPaging = false;
        } else if (canContinueWithCursorDespiteFlag) {
            // Some responses expose a valid advancing cursor but report hasNextPage=false.
            afterCursor = endCursor;
            forceIdGreaterThanPaging = false;
        } else if (canFallbackPaginate) {
            // Some API responses return arrays without pageInfo; continue by id cursor.
            afterCursor = fallbackAfterFromId;
            forceIdGreaterThanPaging = false;
        } else if (canSwitchToIdRangePaging) {
            // Cursor did not advance on a full page; switch to id range paging.
            forceIdGreaterThanPaging = true;
            afterCursor = null;
        } else {
            keepGoing = false;
        }
    }

    // Remove helper-only id if caller didn't ask for it.
    return allRecords.map(record => {
        if (record && typeof record === 'object' && !Object.prototype.hasOwnProperty.call(originalSelect, 'id')) {
            const { id, ...rest } = record;
            return rest;
        }
        return record;
    });
}

function normalizeCollectionTagValue(value) {
    return (value || '').toString().trim().toLowerCase().replace(/\s+/g, '-');
}

function extractConfiguredTags(value) {
    if (value === null || value === undefined) return [];

    if (Array.isArray(value)) {
        return value
            .map(item => (item || '').toString().trim())
            .filter(Boolean);
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return [];

        try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) {
                return parsed
                    .map(item => (item || '').toString().trim())
                    .filter(Boolean);
            }
        } catch (_) {
            // Not JSON, continue with delimiter split fallback.
        }

        if (trimmed.includes(',')) {
            return trimmed.split(',').map(item => item.trim()).filter(Boolean);
        }
        if (trimmed.includes('|')) {
            return trimmed.split('|').map(item => item.trim()).filter(Boolean);
        }

        return [trimmed];
    }

    return [];
}

// Modified loadMakes function to fetch all records
  async function loadMakes(api) {
      try {
          if (api.ProductVehicleFitment) {
              const response = await fetchAllProductVehicleFitments(api, {
                  select: { make: true },
                  filter: {
                      shopId: { equals: window.currentShopId.toString() }
                  }
              });
              
              const makes = [...new Set(response.map(f => f.make))].filter(Boolean);
              return makes.sort((a, b) => a.localeCompare(b));
          } else {
              return [];
          }
      } catch (error) {
          return [];
      }
  }
  
  // Helper function to populate makes dropdown
  function populateMakesDropdown(makeSelect, makes) {
      makeSelect.innerHTML = '<option value="">Select Make</option>' + 
          makes.map(make => `<option value="${make}">${make}</option>`).join('');
      makeSelect.classList.remove('make-loading');
  }
  
  // Modified loadModels function to fetch all records
  async function loadModels(api, make) {
      try {
          if (api.ProductVehicleFitment) {
              const response = await fetchAllProductVehicleFitments(api, {
                  filter: {
                      AND: [
                          { shopId: { equals: window.currentShopId.toString() } },
                          { make: { equals: make } }
                      ]
                  },
                  select: { model: true }
              });
              
              const models = [...new Set(response.map(f => f.model))].filter(Boolean);
              return models.sort((a, b) => a.localeCompare(b));
          } else {
              return [];
          }
      } catch (error) {
          return [];
      }
  }
  
  // Update loadTrims to fetch all records
  async function loadTrims(api, make, model) {
      try {
          if (api.ProductVehicleFitment) {
              const response = await fetchAllProductVehicleFitments(api, {
                  filter: {
                      AND: [
                          { shopId: { equals: window.currentShopId.toString() } },
                          { make: { equals: make } },
                          { model: { equals: model } }
                      ]
                  },
                  select: {
                      trim: true,
                      yearFrom: true,
                      yearTo: true
                  }
              });
              
              const trims = [...new Set(response.map(f => f.trim))].filter(Boolean);
              return {
                  trims: trims.sort((a, b) => a.localeCompare(b)),
                  fitments: response
              };
          } else {
              return { trims: [], fitments: [] };
          }
      } catch (error) {
          return { trims: [], fitments: [] };
      }
  }
  
  // Helper function to process vehicle data
  window.processVehicleData = function(data) {
    // Safety check - ensure data is an object
    if (!data || typeof data !== 'object') {
        return {
              make: null,
              model: null,
              trim: null,
              cabType: null,
              year: null,
              transmission: null,
              fuelType: null
          };
      }
      
      const capitalize = str => str?.charAt(0).toUpperCase() + str?.slice(1)?.toLowerCase();
      const { 
          ModelData: m = {}, 
          VehicleIdentification: v = {}, 
          Transmission: t = {}, 
          BodyDetails: b = {},
          Performance: p = {},
          PowerSource: ps = {},
          DvlaTechnicalDetails: d = {},
          SmmtDetails: s = {}
      } = data;
      
      let trim = '';
      if (m.Model && m.Range) {
          trim = m.Model.replace(m.Range, '').trim()
              .toLowerCase()
              .replace(/^\d{3}[di]\s+/gi, '')  // Remove the engine designation
              .replace(/\b(?:auto|automatic|manual)\b/gi, '')  // Remove transmission type
              .trim();
      } else if (m.Model) {
          trim = m.Model.trim().toLowerCase();
      }
  
      // Add door type if available
      if (b.NumberOfDoors) {
          trim += ` ${b.NumberOfDoors}-door`;
      }
  
      // Process wheelbase information
      let cabType = m.CabType || b.BodyStyle || null;
      if (b.WheelBaseType) {
          const wheelbase = b.WheelBaseType.toLowerCase();
          // Handle numeric wheelbases (90, 110, 130)
          if (wheelbase.includes('90')) {
              cabType = cabType ? `${cabType} SWB` : 'SWB';
          } else if (wheelbase.includes('110')) {
              cabType = cabType ? `${cabType} MWB` : 'MWB';
          } else if (wheelbase.includes('130')) {
              cabType = cabType ? `${cabType} LWB` : 'LWB';
          }
          // Handle text-based wheelbases
          else if (wheelbase.includes('long wheelbase')) {
              cabType = cabType ? `${cabType} LWB` : 'LWB';
          } else if (wheelbase.includes('medium wheelbase')) {
              cabType = cabType ? `${cabType} MWB` : 'MWB';
          } else if (wheelbase.includes('short wheelbase')) {
              cabType = cabType ? `${cabType} SWB` : 'SWB';
          }
      }
      
      // Extract power information
      const powerBhp = p.Power?.Bhp || s.PowerBhp || null;
      const powerKw = p.Power?.Kw || s.PowerKw || d.MaximumNetPower || null;
      
      // Extract drive information - prioritize DrivingAxle field
      const driveType = t.DriveType || s.DriveType || null;
      const drivingAxle = t.DrivingAxle || s.DrivingAxle || null;
      let drive = null;
      if (drivingAxle) {
          // Convert driving axle to readable format
          const drivingAxleLower = drivingAxle.toLowerCase().trim();
          if (drivingAxleLower === 'rear') {
              drive = 'Rear Wheel Drive';
          } else if (drivingAxleLower === 'front') {
              drive = 'Front Wheel Drive';
          } else if (drivingAxleLower === 'all' || drivingAxleLower === 'awd' || drivingAxleLower === '4wd') {
              drive = 'All Wheel Drive';
          } else {
              drive = capitalize(drivingAxle);
          }
      }
      
      // Extract engine description
      const engineDescription = ps.IceDetails?.EngineDescription || s.EngineDescription || null;
      const numberOfCylinders = ps.IceDetails?.NumberOfCylinders || s.NumberOfCylinders || null;
      
      // Format engine info with cylinder count
      let engineInfo = null;
      if (m.FuelType && numberOfCylinders) {
          engineInfo = `${m.FuelType} (${numberOfCylinders} cyl)`;
          if (engineDescription) {
              engineInfo += ` - ${engineDescription}`;
          }
      } else if (m.FuelType) {
          engineInfo = m.FuelType;
          if (engineDescription) {
              engineInfo += ` - ${engineDescription}`;
          }
      }
      
      return {
          make: (() => {
              const make = capitalize(m.Make);
              // Normalize Mercedes variants to just "Mercedes"
              if (make && (make.toLowerCase() === 'mercedes-benz' || make.toLowerCase() === 'mercedes benz')) {
                  return 'Mercedes';
              }
              return make;
          })(),
          model: (() => {
              let baseModel = capitalize(m.Range);
              
              // Check if this is a BMW 4 Series variant
              if (m.Make && m.Make.toLowerCase() === 'bmw' && m.Range && m.Range.toLowerCase().includes('4 series')) {
                  // Check the DVLA model description for body style
                  const dvlaModel = v.DvlaModel || '';
                  if (dvlaModel.toUpperCase().includes('GRAN COUPE')) {
                      baseModel = baseModel + ' Gran Coupe';
                  } else if (b.BodyStyle && b.BodyStyle.toLowerCase().includes('coupe')) {
                      baseModel = baseModel + ' Coupe';
                  }
              }
              
              return baseModel;
          })(),
          trim: trim,
          cabType: cabType,
          year: parseInt(v.YearOfManufacture),
          transmission: t.TransmissionType || s.Transmission || null,
          fuelType: m.FuelType || s.FuelType || null,
          power: powerBhp,
          powerKw: powerKw,
          drive: drive,
          driveType: driveType,
          drivingAxle: drivingAxle,
          engineDescription: engineDescription,
          numberOfCylinders: numberOfCylinders,
          numberOfDoors: b.NumberOfDoors || null,
          numberOfGears: t.NumberOfGears || s.NumberOfGears || null,
          engineCapacity: ps.IceDetails?.EngineCapacityCc || s.EngineCapacity || null,
          torqueNm: p.Torque?.Nm || s.TorqueNm || null,
          torqueLbFt: p.Torque?.LbFt || s.TorqueLbFt || null,
          maxSpeedMph: p.Statistics?.MaxSpeedMph || s.MaxSpeedMph || null,
          maxSpeedKph: p.Statistics?.MaxSpeedKph || s.MaxSpeedKph || null,
          combinedMpg: p.FuelEconomy?.CombinedMpg || s.CombinedMpg || null,
          co2: data.Emissions?.ManufacturerCo2 || s.Co2 || null,
          vin: v.Vin || null,
          engineNumber: v.EngineNumber || null,
          vrm: v.Vrm || null,
          dvlaModel: v.DvlaModel || null
      };
  };
  
  // Helper function to match fitment
  window.matchFitment = function(vehicleData, fitment) {
      // Normalize strings for comparison
      const normalize = str => str?.toLowerCase().replace(/\s+/g, ' ').trim();
      
      const vehicleMakeNorm = normalize(vehicleData.make);
      const vehicleModelNorm = normalize(vehicleData.model);
      const fitmentModelNorm = normalize(fitment.model);
      
      // Detailed logging for debugging
      const debugLog = {
          vehicle: {
              make: vehicleData.make,
              model: vehicleData.model,
              trim: vehicleData.trim,
              year: vehicleData.year,
              cabType: vehicleData.cabType
          },
          fitment: {
              make: fitment.make,
              model: fitment.model,
              trim: fitment.trim,
              yearFrom: fitment.yearFrom,
              yearTo: fitment.yearTo,
              cabType: fitment.cabType
          },
          checks: {}
      };
      
      // Special handling for BMW 4 Series
      if (vehicleMakeNorm === 'bmw' && vehicleModelNorm?.includes('4 series')) {
          const vehicleDoors = vehicleData.numberOfDoors;
          
          // For newer generation where both are "4 Series Coupe" in the model name
          // but we need to use door count to differentiate
          if (vehicleModelNorm === '4 series coupe' && fitmentModelNorm === '4 series') {
              const fitmentCabNorm = normalize(fitment.cabType);
              
              // 2 doors = standard Coupe, should NOT match Gran Coupe fitments
              if (vehicleDoors === 2) {
                  // Match only if fitment is NOT Gran Coupe
                  if (fitmentCabNorm?.includes('gran coupe')) {
                      return false; // Don't match Gran Coupe fitments for 2-door
                  }
              }
              // 4 doors = Gran Coupe, should match Gran Coupe fitments
              else if (vehicleDoors === 4) {
                  // Only match if fitment IS Gran Coupe
                  if (!fitmentCabNorm?.includes('gran coupe')) {
                      return false; // Don't match non-Gran Coupe fitments for 4-door
                  }
              }
          }
          
          // For older generation where model explicitly states Coupe vs Gran Coupe
          if (vehicleModelNorm === '4 series gran coupe' && fitmentModelNorm === '4 series coupe') {
              return false; // Don't match standard Coupe fitments for Gran Coupe
          }
          if (vehicleModelNorm === '4 series coupe' && fitmentModelNorm === '4 series gran coupe') {
              return false; // Don't match Gran Coupe fitments for standard Coupe
          }
      }
      
      // Special handling for Volkswagen Golf performance variants
      if (vehicleMakeNorm === 'volkswagen' && vehicleModelNorm === 'golf') {
          const vehicleTrimNorm = normalize(vehicleData.trim);
          const fitmentTrimNorm = normalize(fitment.trim);
      
          // Check if this is a performance variant (GTI, GTD, R, R-Line)
          const isPerformanceVehicle = vehicleTrimNorm?.match(/\b(gti|gtd|\br\b|r-line|r line)\b/i);
          const isPerformanceFitment = fitmentTrimNorm?.match(/\b(gti|gtd|\br\b|r-line|r line)\b/i);
      
          debugLog.checks.volkswagenGolf = {
              vehicleTrimNorm: vehicleTrimNorm,
              fitmentTrimNorm: fitmentTrimNorm,
              isPerformanceVehicle: !!isPerformanceVehicle,
              isPerformanceFitment: !!isPerformanceFitment
          };
      
          // Performance vehicles should ONLY match performance fitments
          if (isPerformanceVehicle && !isPerformanceFitment) {
              return false; // Performance vehicle, but not a performance fitment - no match
          }
      
          // Non-performance vehicles should NOT match performance fitments
          if (!isPerformanceVehicle && isPerformanceFitment) {
              return false; // Standard vehicle, but performance fitment - no match
          }
      }
      
      // Special handling for Volkswagen R-Line variants (Tiguan, Touareg, and others)
      // Normalize hyphens and spaces to be equivalent for R-Line detection
      const normalizeForRLine = (str) => {
          if (!str) return '';
          return normalize(str).replace(/-/g, ' ').replace(/\s+/g, ' ').trim();
      };
      
      // Apply R-Line matching to all Volkswagen models that support R-Line variants
      const volkswagenRLineModels = ['tiguan', 'touareg', 'passat', 'polo', 'arteon', 't-cross', 't-roc'];
      if (vehicleMakeNorm === 'volkswagen' && volkswagenRLineModels.includes(vehicleModelNorm)) {
          const vehicleTrimNorm = normalizeForRLine(vehicleData.trim);
          const fitmentTrimNorm = normalizeForRLine(fitment.trim);
      
          // Check if this is an R-Line variant (now handles both "r-line" and "r line")
          const isRLineVehicle = vehicleTrimNorm?.match(/\br line\b/i);
          const isRLineFitment = fitmentTrimNorm?.match(/\br line\b/i);
      
          debugLog.checks.volkswagenRLine = {
              model: vehicleModelNorm,
              vehicleTrimNorm: vehicleTrimNorm,
              fitmentTrimNorm: fitmentTrimNorm,
              isRLineVehicle: !!isRLineVehicle,
              isRLineFitment: !!isRLineFitment
          };
      
          // R-Line vehicles should ONLY match R-Line fitments
          if (isRLineVehicle && !isRLineFitment) {
              return false; // R-Line vehicle, but not an R-Line fitment - no match
          }
      
          // Non-R-Line vehicles should NOT match R-Line fitments
          if (!isRLineVehicle && isRLineFitment) {
              return false; // Standard vehicle, but R-Line fitment - no match
          }
      }
      
      // Special handling for Audi trim variants
      let isAudiBlackEditionToSLine = false;
      let isAudiTrimMatch = false;
      if (vehicleMakeNorm === 'audi') {
          const vehicleTrimNorm = normalize(vehicleData.trim);
          const fitmentTrimNorm = normalize(fitment.trim);
      
          // More aggressive normalization for Audi trims - remove common suffixes
          const normalizeAudiTrim = (trim) => {
              if (!trim) return '';
              return trim
                  .toLowerCase()
                  .replace(/\s+/g, ' ')
                  .trim()
                  // Remove engine descriptors
                  .replace(/\b(tdi|tfsi|tsi|fsi|petrol|diesel)\b/gi, '')
                  // Remove transmission descriptors
                  .replace(/\b(quattro|4wd|awd|fwd)\b/gi, '')
                  // Remove door descriptors
                  .replace(/\b(\d+-door)\b/gi, '')
                  // Remove year ranges
                  .replace(/\b\d{4}-\d{4}\b/gi, '')
                  // Clean up multiple spaces
                  .replace(/\s+/g, ' ')
                  .trim();
          };
        
          const vehicleTrimCleaned = normalizeAudiTrim(vehicleTrimNorm);
          const fitmentTrimCleaned = normalizeAudiTrim(fitmentTrimNorm);
      
          // Map Black Edition to S Line (handle both "s line" and "sline")
          if (vehicleTrimCleaned?.includes('black edition') && 
              (fitmentTrimCleaned?.includes('s line') || fitmentTrimCleaned?.includes('sline'))) {
              // Allow Black Edition vehicles to match S Line fitments
              // The rest of the matching logic (year, cab type, etc.) will still apply
              isAudiBlackEditionToSLine = true;
          }
          
          // Enhanced keyword-based matching for Audi trims
          if (vehicleTrimCleaned && fitmentTrimCleaned && !isAudiBlackEditionToSLine) {
              // Extract keywords by removing noise words and engine designations
              const extractKeywords = (trim) => {
                  return trim
                      // Remove standalone numbers (engine designations like 40, 50)
                      .replace(/\b\d+\b/g, '')
                      // Remove noise words
                      .replace(/\b(semi|auto|automatic|manual)\b/gi, '')
                      // Remove standalone 'e' (often appears in hybrid trims)
                      .replace(/\b e \b/gi, ' ')
                      .replace(/\b e$/gi, '')
                      .replace(/^e \b/gi, '')
                      // Handle s line variations
                      .replace(/\bsline\b/gi, 's line')
                      // Clean up hyphens and multiple spaces
                      .replace(/-+/g, ' ')
                      .replace(/\s+/g, ' ')
                      .trim()
                      .split(' ')
                      .filter(word => word.length > 0);
              };
              
              const vehicleKeywords = extractKeywords(vehicleTrimCleaned);
              const fitmentKeywords = extractKeywords(fitmentTrimCleaned);
              
              // Check if all fitment keywords appear in vehicle keywords (order-independent)
              if (fitmentKeywords.length > 0) {
                  const allKeywordsMatch = fitmentKeywords.every(fitmentWord => {
                      const matches = vehicleKeywords.some(vehicleWord => {
                          const exactMatch = vehicleWord === fitmentWord;
                          const vehicleIncludes = vehicleWord.includes(fitmentWord);
                          const fitmentIncludes = fitmentWord.includes(vehicleWord);
                          const result = exactMatch || vehicleIncludes || fitmentIncludes;
                          return result;
                      });
                      return matches;
                  });
                  
                  if (allKeywordsMatch) {
                      isAudiTrimMatch = true;
                  }
              }
          }
      
          // If vehicle is Black Edition but fitment is NOT S Line, continue with normal matching
          // If vehicle is NOT Black Edition and fitment IS S Line, continue with normal matching
      }

      // Special handling for Audi cab type matching
      let isAudiCabTypeInTrim = false;
      if (vehicleMakeNorm === 'audi') {
          const vehicleTrimNorm = normalize(vehicleData.trim);
          const fitmentCabNorm = normalize(fitment.cabType);
      
          // Check if fitment cab type (sportback/saloon) appears in vehicle trim
          if (fitmentCabNorm && vehicleTrimNorm) {
              if ((fitmentCabNorm.includes('sportback') && vehicleTrimNorm.includes('sportback')) ||
                  (fitmentCabNorm.includes('saloon') && vehicleTrimNorm.includes('saloon'))) {
                  isAudiCabTypeInTrim = true;
              }
          }
      }

      // Check year range
      const yearMatches = +vehicleData.year >= +fitment.yearFrom && +vehicleData.year <= +fitment.yearTo;
      debugLog.checks.yearMatch = {
          result: yearMatches,
          vehicleYear: vehicleData.year,
          fitmentYearFrom: fitment.yearFrom,
          fitmentYearTo: fitment.yearTo,
          reason: yearMatches ? 'Year is within range' : `Year ${vehicleData.year} is outside range ${fitment.yearFrom}-${fitment.yearTo}`
      };
      
      // Check trim - handle multiple trims separated by "/"
      // Normalize hyphens and spaces to be equivalent (e.g., "r-line" = "r line")
      const normalizeTrim = (str) => {
          if (!str) return '';
          return normalize(str)
              .replace(/-/g, ' ')  // Replace hyphens with spaces
              .replace(/\s+/g, ' ')  // Normalize multiple spaces
              .trim();
      };
      
      const vehicleTrimNorm = normalizeTrim(vehicleData.trim);
      const fitmentTrims = normalizeTrim(fitment.trim).split('/').map(t => t.trim()).filter(t => t.length > 0);
    
      // Match if any of the "/" separated trims match, or if it's the Audi Black Edition to S Line mapping, or if Audi keyword matching succeeded
      const trimMatches = isAudiBlackEditionToSLine || isAudiTrimMatch || !vehicleTrimNorm || !fitment.trim || fitmentTrims.length === 0 ||
                         fitmentTrims.some(trim => {
                             // Normalize both for comparison (hyphens already normalized, but ensure consistency)
                             const normalizedVehicleTrim = normalizeTrim(vehicleTrimNorm);
                             const normalizedFitmentTrim = normalizeTrim(trim);
                             
                             // Check if the normalized fitment trim appears in the vehicle trim (word boundary aware)
                             // This handles cases like "c6 r-line tdi" matching "r line"
                             const vehicleContainsFitment = normalizedVehicleTrim.includes(normalizedFitmentTrim);
                             
                             // Check if vehicle trim appears in fitment (for exact or longer matches)
                             const fitmentContainsVehicle = normalizedFitmentTrim.includes(normalizedVehicleTrim);
                             
                             // Word-boundary matching for R-Line specifically (handles "r-line" vs "r line")
                             // After normalization, hyphens are converted to spaces, so just check for "r line"
                             const vehicleHasRLine = /\br\s+line\b/i.test(normalizedVehicleTrim);
                             const fitmentHasRLine = /\br\s+line\b/i.test(normalizedFitmentTrim);
                             const rLineMatch = vehicleHasRLine && fitmentHasRLine;
                             
                             // For word-boundary matching, check if fitment trim appears as complete words in vehicle trim
                             const fitmentWords = normalizedFitmentTrim.split(/\s+/).filter(w => w.length > 0);
                             const allWordsMatch = fitmentWords.length > 0 && fitmentWords.every(word => {
                                 // Check if word appears with word boundaries in vehicle trim
                                 const wordRegex = new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
                                 return wordRegex.test(normalizedVehicleTrim);
                             });
                             
                             return vehicleContainsFitment || fitmentContainsVehicle || rLineMatch || allWordsMatch;
                         });
      debugLog.checks.trimMatch = {
          result: trimMatches,
          vehicleTrim: vehicleData.trim,
          vehicleTrimNorm: vehicleTrimNorm,
          fitmentTrim: fitment.trim,
          fitmentTrims: fitmentTrims,
          isAudiBlackEditionToSLine: isAudiBlackEditionToSLine,
          isAudiTrimMatch: isAudiTrimMatch,
          reason: trimMatches ? 'Trim matches' : `Trim mismatch: vehicle="${vehicleTrimNorm}" vs fitment="${fitmentTrims.join('/')}"`,
          details: fitmentTrims.map(trim => ({
              fitmentTrim: trim,
              vehicleContainsFitment: vehicleTrimNorm.includes(trim),
              fitmentContainsVehicle: trim.includes(vehicleTrimNorm),
              vehicleHasRLine: vehicleTrimNorm.includes('r line'),
              fitmentHasRLine: trim.includes('r line')
          }))
      };
      
      // Enhanced cab type matching with wheelbase handling
      const vehicleCabNorm = normalize(vehicleData.cabType);
      const fitmentCabNorm = normalize(fitment.cabType);
      
      // Check if vehicle is convertible and fitment is coupe (or vice versa) - treat as equivalent
      // Also handle cases where fitment cabType might be empty but vehicle is convertible
      const vehicleIsConvertible = vehicleCabNorm.includes('convertible');
      const fitmentIsCoupe = fitmentCabNorm.includes('coupe');
      const vehicleIsCoupe = vehicleCabNorm.includes('coupe');
      const fitmentIsConvertible = fitmentCabNorm.includes('convertible');
      
      // Convertible matches Coupe (or empty fitment cabType if vehicle is convertible)
      const isConvertibleToCoupe = (vehicleIsConvertible && (fitmentIsCoupe || !fitmentCabNorm)) ||
                                   (vehicleIsCoupe && fitmentIsConvertible);
  
      const cabTypeMatches = isAudiCabTypeInTrim || !vehicleCabNorm || !fitmentCabNorm || 
                            isConvertibleToCoupe ||
                            vehicleCabNorm.includes(fitmentCabNorm) || 
                            fitmentCabNorm.includes(vehicleCabNorm) ||
                            (vehicleCabNorm.includes('coupe') && fitmentCabNorm.includes('coupe')) ||
                            // Handle wheelbase matching - text to text
                            (vehicleCabNorm.includes('lwb') && fitmentCabNorm.includes('long wheelbase')) ||
                            (vehicleCabNorm.includes('swb') && fitmentCabNorm.includes('short wheelbase')) ||
                            (vehicleCabNorm.includes('mwb') && fitmentCabNorm.includes('medium wheelbase')) ||
                            (fitmentCabNorm.includes('lwb') && vehicleCabNorm.includes('long wheelbase')) ||
                            (fitmentCabNorm.includes('swb') && vehicleCabNorm.includes('short wheelbase')) ||
                            (fitmentCabNorm.includes('mwb') && vehicleCabNorm.includes('medium wheelbase')) ||
                            // Handle wheelbase matching - numeric to abbreviation
                            (vehicleCabNorm.includes('swb') && fitmentCabNorm.includes('90')) ||
                            (vehicleCabNorm.includes('mwb') && fitmentCabNorm.includes('110')) ||
                            (vehicleCabNorm.includes('lwb') && fitmentCabNorm.includes('130')) ||
                            (fitmentCabNorm.includes('swb') && vehicleCabNorm.includes('90')) ||
                            (fitmentCabNorm.includes('mwb') && vehicleCabNorm.includes('110')) ||
                            (fitmentCabNorm.includes('lwb') && vehicleCabNorm.includes('130'));
      debugLog.checks.cabTypeMatch = {
          result: cabTypeMatches,
          vehicleCabType: vehicleData.cabType,
          vehicleCabNorm: vehicleCabNorm,
          fitmentCabType: fitment.cabType,
          fitmentCabNorm: fitmentCabNorm,
          isAudiCabTypeInTrim: isAudiCabTypeInTrim,
          isConvertibleToCoupe: isConvertibleToCoupe,
          reason: cabTypeMatches ? 
              (isConvertibleToCoupe ? 'Cab type matches (Convertible = Coupe)' : 'Cab type matches') : 
              `Cab type mismatch: vehicle="${vehicleCabNorm}" vs fitment="${fitmentCabNorm}"`
      };
      
      const finalResult = yearMatches && trimMatches && cabTypeMatches;
      debugLog.finalResult = finalResult;
      return finalResult;
  };
  
  function showNoProductsMessage() {
      const popup = document.createElement('div');
      popup.className = 'no-products-popup-overlay';
      popup.innerHTML = `
          <div class="no-products-popup">
              <button class="close-popup">×</button>
              <h3>No Products Found</h3>
              <p>Sorry, there are no products available for your selected vehicle. Here are some things you can try:</p>
              <ul>
                  <li>Ensure your registration number is correct</li>
                  <li>Use the menu available on the website to find your vehicle</li>
                  <li>Try a different vehicle model or year</li>
              </ul>
              <button class="browse-button" onclick="window.location.href='/collections/all'">Browse All Products</button>
          </div>
      `;
      document.body.appendChild(popup);
  
      // Fix close button functionality
      const closeButton = popup.querySelector('.close-popup');
      const closePopup = () => popup.remove();
      
      popup.addEventListener('click', (e) => {
          if (e.target === popup) closePopup();
      });
      
      closeButton.addEventListener('click', closePopup);
  }
  
  // Helper function to log failed vehicle searches
  async function logFailedSearch(api, registration, vehicleData, errorReason) {
      try {
          await api.failedSearch.create({
              registration: registration,
              vehicleData: vehicleData || {},
              errorReason: errorReason,
              shop: { _link: window.currentShopId.toString() }
          });
      } catch (error) {
          // Silently fail - don't disrupt user experience
      }
  }

  // Helper function to log successful vehicle searches
  async function logVehicleSearch(api, searchMethod, vehicleData, registration, collectionUrl) {
      try {
          // Generate or retrieve session ID
          let sessionId = localStorage.getItem('vs_session_id');
          if (!sessionId) {
              sessionId = 'vs_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
              localStorage.setItem('vs_session_id', sessionId);
          }
          
          await api.vehicleSearchSession.create({
              shop: { _link: window.currentShopId.toString() },
              searchMethod: searchMethod,
              vehicleData: vehicleData || {},
              registration: registration || null,
              searchTimestamp: new Date().toISOString(),
              collectionUrl: collectionUrl || null,
              sessionId: sessionId,
              converted: false
          });
      } catch (error) {
          // Silently fail - don't disrupt user experience
      }
  }

  // Helper function to show a toast notification
  function showToast(message, duration = 2000) {
      const toast = document.createElement('div');
      toast.className = 'vehicle-search-toast';
      toast.textContent = message;
      toast.style.cssText = `
          position: fixed;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          background-color: #323232;
          color: white;
          padding: 12px 24px;
          border-radius: 4px;
          font-size: 14px;
          z-index: 10001;
          animation: slideUp 0.3s ease-out;
      `;
      
      // Add animation styles if not already present
      if (!document.getElementById('toast-styles')) {
          const style = document.createElement('style');
          style.id = 'toast-styles';
          style.textContent = `
              @keyframes slideUp {
                  from {
                      opacity: 0;
                      transform: translateX(-50%) translateY(20px);
                  }
                  to {
                      opacity: 1;
                      transform: translateX(-50%) translateY(0);
                  }
              }
          `;
          document.head.appendChild(style);
      }
      
      document.body.appendChild(toast);
      
      setTimeout(() => {
          toast.style.opacity = '0';
          toast.style.transition = 'opacity 0.3s';
          setTimeout(() => toast.remove(), 300);
      }, duration);
  }

  // Helper function to show success UI with save to garage option
  async function showSuccessUI(vehicleData, fitmentToStore, searchMethod) {
      const resolved = await window.determineCollectionUrlAsync(fitmentToStore);
      const resolvedFitment = resolved.fitment || fitmentToStore;
      const resolvedCollectionUrl = resolved.url;

      // Dispatch save-to-garage event for both manual and registration searches
      const saveEvent = new CustomEvent('sc-vehicle-search:save-to-garage', {
          detail: {
              make: vehicleData.make,
              model: vehicleData.model,
              year: vehicleData.year,
              trim: vehicleData.trim || null,
              registration: vehicleData.registration || null,
              cabType: vehicleData.cabType || null,
              transmission: vehicleData.transmission || null,
              fuelType: vehicleData.fuelType || null,
              collectionUrl: resolvedCollectionUrl
          },
          bubbles: true
      });
      document.dispatchEvent(saveEvent);
      
      // Show toast notification
      const message = searchMethod === 'registration' 
          ? 'Vehicle found! Redirecting...' 
          : 'Vehicle saved! Redirecting...';
      showToast(message, 1500);
      
      // Navigate immediately for both search methods
      setTimeout(() => {
          window.navigateToCollection(resolvedFitment, searchMethod);
      }, 100);
  }

  // Helper to normalise the universalTags field coming from the API.
  // The field can arrive as a real JS array, a JSON-stringified array, or
  // null/undefined – this function always returns a flat array of non-empty strings.
  function extractUniversalTags(value) {
      if (value === null || value === undefined) return [];
      if (Array.isArray(value)) {
          const result = value.filter(item => typeof item === 'string' && item.length > 0);
          return result;
      }
      if (typeof value === 'string') {
          try {
              const parsed = JSON.parse(value);
              if (Array.isArray(parsed)) {
                  const result = parsed.filter(item => typeof item === 'string' && item.length > 0);
                  return result;
              }
          } catch (_) {
              // not valid JSON – fall through
          }
      }
      return [];
  }

  // Backfill universalTags when the matched fitment payload does not include it.
  async function hydrateUniversalTagsIfMissing(api, fitment) {
      if (!fitment || typeof fitment !== 'object') return fitment;

      const existing = extractUniversalTags(fitment.universalTags ?? fitment.universalTag);
      if (existing.length > 0) return fitment;

      if (!api?.ProductVehicleFitment || !fitment.tag) {
          return fitment;
      }

      try {
          const fallbackResults = await fetchAllProductVehicleFitments(api, {
              filter: {
                  AND: [
                      { shopId: { equals: window.currentShopId.toString() } },
                      { tag: { equals: fitment.tag } }
                  ]
              },
              select: {
                  tag: true,
                  universalTags: true
              }
          });

          const fallbackArray = Array.isArray(fallbackResults)
              ? fallbackResults
              : (fallbackResults?.data || fallbackResults?.items || []);
          const rowWithTags = fallbackArray.find(row => {
              const tags = extractUniversalTags(row?.universalTags ?? row?.universalTag);
              return tags.length > 0;
          });

          if (rowWithTags) {
              return {
                  ...fitment,
                  universalTags: rowWithTags.universalTags
              };
          }

          // Secondary fallback: query by make/model/year and match tag after normalization.
          if (fitment.make && fitment.model) {
              const secondaryResults = await fetchAllProductVehicleFitments(api, {
                  filter: {
                      AND: [
                          { shopId: { equals: window.currentShopId.toString() } },
                          { lowercaseMake: { equals: fitment.make.toLowerCase() } },
                          { lowercaseModel: { equals: fitment.model.toLowerCase() } }
                      ]
                  },
                  select: {
                      tag: true,
                      universalTags: true,
                      yearFrom: true,
                      yearTo: true
                  }
              });

              const secondaryArray = Array.isArray(secondaryResults)
                  ? secondaryResults
                  : (secondaryResults?.data || secondaryResults?.items || []);

              const normalizeTag = (value) => (value || '').toString().trim().toLowerCase().replace(/\s+/g, '-');
              const wantedTag = normalizeTag(fitment.tag);
              const fitmentYearFrom = parseInt(fitment.yearFrom);
              const fitmentYearTo = parseInt(fitment.yearTo);

              const secondaryMatch = secondaryArray.find(row => {
                  const rowTags = extractUniversalTags(row?.universalTags ?? row?.universalTag);
                  if (rowTags.length === 0) return false;

                  const rowTagNormalized = normalizeTag(row?.tag);
                  const tagMatches = wantedTag && rowTagNormalized && rowTagNormalized === wantedTag;
                  if (tagMatches) return true;

                  const rowYearFrom = parseInt(row?.yearFrom);
                  const rowYearTo = parseInt(row?.yearTo);
                  const yearsOverlap =
                      Number.isFinite(fitmentYearFrom) &&
                      Number.isFinite(fitmentYearTo) &&
                      Number.isFinite(rowYearFrom) &&
                      Number.isFinite(rowYearTo) &&
                      fitmentYearFrom <= rowYearTo &&
                      fitmentYearTo >= rowYearFrom;

                  return yearsOverlap;
              });

              if (secondaryMatch) {
                  return {
                      ...fitment,
                      universalTags: secondaryMatch.universalTags
                  };
              }

              // Tertiary fallback: case-sensitive fields in case lowercase* fields are not populated
              const tertiaryResults = await fetchAllProductVehicleFitments(api, {
                  filter: {
                      AND: [
                          { shopId: { equals: window.currentShopId.toString() } },
                          { make: { equals: fitment.make } },
                          { model: { equals: fitment.model } }
                      ]
                  },
                  select: {
                      tag: true,
                      universalTags: true,
                      yearFrom: true,
                      yearTo: true
                  }
              });

              const tertiaryArray = Array.isArray(tertiaryResults)
                  ? tertiaryResults
                  : (tertiaryResults?.data || tertiaryResults?.items || []);

              const normalizeTagTertiary = (value) => (value || '').toString().trim().toLowerCase().replace(/\s+/g, '-');
              const wantedTagTertiary = normalizeTagTertiary(fitment.tag);
              const tertiaryMatch = tertiaryArray.find(row => {
                  const rowTags = extractUniversalTags(row?.universalTags ?? row?.universalTag);
                  if (rowTags.length === 0) return false;
                  return normalizeTagTertiary(row?.tag) === wantedTagTertiary;
              });

              if (tertiaryMatch) {
                  return {
                      ...fitment,
                      universalTags: tertiaryMatch.universalTags
                  };
              }
          }
      } catch (error) {
          // Silently fail
      }
      return fitment;
  }

  // Helper function to determine collection URL from fitment data
  window.determineCollectionUrl = function(fitment) {
      // 1. First check for assigned collections
      if (fitment.collections && fitment.collections.length > 0) {
          const collectionHandle = fitment.collections[0].handle;
          return `/collections/${collectionHandle}`;
      }

      // 2. If fitment has explicit product tags configured in app backend, use those.
      const configuredTags = extractConfiguredTags(
          fitment.customTags ?? fitment.customTag ?? fitment.tags
      );
      if (configuredTags.length > 0) {
          const normalizedConfiguredTags = configuredTags.map(normalizeCollectionTagValue);
          const tagsPath = normalizedConfiguredTags.join('+');
          return `/collections/all/${tagsPath}`;
      }

      // 3. Determine fitmentTag: use fitment.tag if present, otherwise construct from vehicle details
      let fitmentTag;
      if (fitment.tag) {
          fitmentTag = fitment.tag;
      } else {
          let make = fitment.make;
          let model = fitment.model;
          let trim = fitment.trim;
          let yearFrom = fitment.yearFrom;
          let yearTo = fitment.yearTo;

          if ((!make || !model) && typeof localStorage !== 'undefined') {
              try {
                  const storedVehicle = localStorage.getItem('selectedVehicle');
                  if (storedVehicle) {
                      const vehicle = JSON.parse(storedVehicle);
                      make = make || vehicle.make;
                      model = model || vehicle.model;
                      trim = trim || vehicle.trim;
                      yearFrom = yearFrom || vehicle.yearFrom || vehicle.year;
                      yearTo = yearTo || vehicle.yearTo || vehicle.year;
                  }
              } catch (e) {
                  // Silently fail
              }
          }

          if (make && model) {
              fitmentTag = `${make.toLowerCase()}-${model.toLowerCase()}`;
              if (trim) {
                  fitmentTag += `-${trim.toLowerCase().replace(/\s+/g, '-')}`;
              }
              if (yearFrom && yearTo) {
                  fitmentTag += `-${yearFrom}-${yearTo}`;
              }
          } else {
              fitmentTag = '';
          }
      }

      if (fitmentTag) {
          let tag = normalizeCollectionTagValue(fitmentTag);
          const rawUniversalTags = fitment.universalTags ?? fitment.universalTag;
          const uTags = extractUniversalTags(rawUniversalTags);
          if (uTags.length > 0) {
              uTags.forEach(universalTag => {
                  tag += '+' + normalizeCollectionTagValue(universalTag);
              });
          }
          return `/collections/all/${tag}`;
      }

      // 4. Fall back to all collections if nothing works
      return `/collections/all`;
  };

  window.determineCollectionUrlAsync = async function(fitment) {
      const api = window.vehicleSearchApi || window.vehicleApi;
      const hydratedFitment = await hydrateUniversalTagsIfMissing(api, fitment);
      return {
          url: window.determineCollectionUrl(hydratedFitment),
          fitment: hydratedFitment
      };
  };

  window.navigateToCollection = async function(fitment, searchMethod) {
      const resolved = await window.determineCollectionUrlAsync(fitment);
      const resolvedFitment = resolved.fitment || fitment;
      const url = resolved.url;

      // Update stored fitment
      try {
          localStorage.setItem('selectedFitment', JSON.stringify(resolvedFitment));
      } catch (e) {
          // Silently fail
      }

      window.location.href = url;
  };
  
  // Add this helper function at the top level
  function areAllVisibleFieldsFilled() {
      const visibleFields = [
          { id: 'make', select: document.getElementById('make') },
          { id: 'model', select: document.getElementById('model') },
          { id: 'trim-field', select: document.getElementById('trim') },
          { id: 'cab-type-field', select: document.getElementById('cabType') },
          { id: 'transmission-field', select: document.getElementById('transmission') },
          { id: 'year-field', select: document.getElementById('year') }
      ];
  
      return visibleFields.every(field => {
          const fieldElement = document.getElementById(field.id);
          // If field is not visible, skip it in the check
          if (!fieldElement || fieldElement.style.display === 'none') {
              return true;
          }
          // If field is visible, check if it has a value
          return field.select.value !== '';
      });
  }
  
  // Helper function to populate models dropdown
  function populateModelsDropdown(modelSelect, models) {
      modelSelect.innerHTML = '<option value="">Select Model</option>' + 
          models.map(model => `<option value="${model}">${model}</option>`).join('');
      modelSelect.disabled = false;
  }

  // Helper functions
  function resetDependentFields() {
      // Reset and hide all fields
      const fields = {
          trim: { select: document.getElementById('trim'), field: document.getElementById('trim-field') },
          transmission: { select: document.getElementById('transmission'), field: document.getElementById('transmission-field') },
          cabType: { select: document.getElementById('cabType'), field: document.getElementById('cab-type-field') },
          year: { select: document.getElementById('year'), field: document.getElementById('year-field') }
      };
  
      Object.values(fields).forEach(({ select, field }) => {
          if (select) {
              select.innerHTML = `<option value="">Select ${select.id.charAt(0).toUpperCase() + select.id.slice(1)}</option>`;
              select.disabled = true;
          }
          if (field) field.style.display = 'none';
      });
  
      const secondaryFields = document.querySelector('.secondary-fields');
      if (secondaryFields) secondaryFields.style.display = 'none';
  }
  
  function showTrimField(trims) {
      const trimSelect = document.getElementById('trim');
      const trimField = document.getElementById('trim-field');
      
      if (trimSelect && trimField) {
          trimField.style.display = 'block';
          trimSelect.innerHTML = '<option value="">Select Trim</option>' +
              trims.map(trim => `<option value="${trim}">${trim}</option>`).join('');
          trimSelect.disabled = false;
      }
  }
  
  function showTransmissionField(transmissions) {
      const transmissionSelect = document.getElementById('transmission');
      const transmissionField = document.getElementById('transmission-field');
      
      if (transmissionSelect && transmissionField) {
          transmissionField.style.display = 'block';
          transmissionSelect.innerHTML = '<option value="">Select Transmission</option>' +
              transmissions.map(trans => `<option value="${trans}">${trans}</option>`).join('');
          transmissionSelect.disabled = false;
      }
  }
  
  function showCabTypeField(cabTypes) {
      const cabTypeSelect = document.getElementById('cabType');
      const cabTypeField = document.getElementById('cab-type-field');
      
      if (cabTypeSelect && cabTypeField) {
          cabTypeField.style.display = 'block';
          cabTypeSelect.innerHTML = '<option value="">Select Cab Type</option>' +
              cabTypes.map(type => `<option value="${type}">${type}</option>`).join('');
          cabTypeSelect.disabled = false;
      }
  }
  
  function processYearsFromFitments(fitments, yearSelect) {
      if (!yearSelect || !fitments?.length) return;
  
      const years = new Set();
      fitments.forEach(f => {
          if (f.yearFrom && f.yearTo) {
              for (let y = parseInt(f.yearFrom); y <= parseInt(f.yearTo); y++) {
                  years.add(y);
              }
          }
      });
  
      if (years.size > 0) {
          // Show the year field container
          const yearField = document.getElementById('year-field');
          if (yearField) {
              yearField.style.display = 'block';
          }

          // Populate and enable the select
          yearSelect.innerHTML = '<option value="">Select Year</option>' +
              [...years].sort().map(year => `<option value="${year}">${year}</option>`).join('');
          yearSelect.disabled = false;
          
          const secondaryFields = document.querySelector('.secondary-fields');
          if (secondaryFields) {
              secondaryFields.style.display = 'flex';
          }
      }
  }

  // Setup dropdown event listeners - extracted to a separate function
  async function setupDropdownEventListeners() {
      const makeSelect = document.getElementById('make');
      
      // Check if already initialized by seeing if make select already has options beyond the default
      if (makeSelect && makeSelect.options.length > 1) {
          return; // Already populated, don't re-initialize
      }
      
      const modelSelect = document.getElementById('model');
      const trimSelect = document.getElementById('trim');
      const yearSelect = document.getElementById('year');
      const cabTypeSelect = document.getElementById('cabType');
      const transmissionSelect = document.getElementById('transmission');
      const api = window.vehicleSearchApi || window.vehicleApi;
      
      if (!makeSelect || !modelSelect || !api) {
          return;
      }

      // Load makes dropdown from API
      if (api.ProductVehicleFitment) {
          try {
              // Show loading state
              makeSelect.innerHTML = '<option value="">Loading makes...</option>';
              makeSelect.disabled = true;
              
              // Load makes from API
              const response = await fetchAllProductVehicleFitments(api, {
                  select: { make: true },
                  filter: {
                      shopId: { equals: window.currentShopId.toString() }
                  }
              });
              
              const makes = [...new Set(response.map(f => f.make))].filter(Boolean);
              const sortedMakes = makes.sort((a, b) => a.localeCompare(b));
              
              // Populate dropdown
              makeSelect.innerHTML = '<option value="">Select Make</option>' + 
                  sortedMakes.map(make => `<option value="${make}">${make}</option>`).join('');
              makeSelect.disabled = false;
              
          } catch (error) {
              makeSelect.innerHTML = '<option value="">Select Make</option>';
              makeSelect.disabled = false;
          }
      }

      // Add make selection change handler
      makeSelect.addEventListener('change', async function() {
          const selectedMake = this.value;
          
          // Reset dependent fields
          resetDependentFields();

          if (selectedMake) {
              try {
                   const filterToApply = {
                       AND: [
                           { shopId: { equals: window.currentShopId.toString() } },
                           { make: { equals: selectedMake } }
                       ]
                   };
                   
                   const fitments = await fetchAllProductVehicleFitments(api, {
                       filter: filterToApply,
                       select: {
                           model: true
                       }
                   });

                      const fitmentsArray = Array.isArray(fitments) ? fitments : (fitments?.data || fitments?.items || []);
                   const models = [...new Set(fitmentsArray.map(f => f.model))].filter(Boolean).sort();
                  modelSelect.innerHTML = '<option value="">Select Model</option>' +
                      models.map(model => `<option value="${model}">${model}</option>`).join('');
                  modelSelect.disabled = false;

              } catch (error) {
                  const secondaryFields = document.querySelector('.secondary-fields');
                  if (secondaryFields) secondaryFields.style.display = 'none';
              }
          }
      });

      // Model selection change handler
      if (modelSelect) {
          modelSelect.addEventListener('change', async function() {
              const selectedMake = makeSelect?.value;
              const selectedModel = this.value;

              // Reset dependent fields
              resetDependentFields();

              if (selectedMake && selectedModel) {
                  try {
                       const filterToApply = {
                           AND: [
                               { shopId: { equals: window.currentShopId.toString() } },
                               { make: { equals: selectedMake } },
                               { model: { equals: selectedModel } }
                           ]
                       };
                       
                       const fitments = await fetchAllProductVehicleFitments(api, {
                           filter: filterToApply,
                           select: {
                               trim: true,
                               cabType: true,
                               transmission: true,
                               yearFrom: true,
                               yearTo: true
                           }
                       });

                       const fitmentsArray = Array.isArray(fitments) ? fitments : (fitments?.data || fitments?.items || []);

                       // Check what fields we have in the fitments
                      const trims = [...new Set(fitmentsArray.map(f => f.trim))].filter(Boolean).sort();
                      const cabTypes = [...new Set(fitmentsArray.map(f => f.cabType))].filter(Boolean).sort();
                      const transmissions = [...new Set(fitmentsArray.map(f => f.transmission))].filter(Boolean).sort();

                      const secondaryFields = document.querySelector('.secondary-fields');
                      if (secondaryFields) secondaryFields.style.display = 'flex';

                      // If we have no additional fields, show years directly
                      if (trims.length === 0 && cabTypes.length === 0 && transmissions.length === 0) {
                          processYearsFromFitments(fitmentsArray, yearSelect);
                          return;
                      }

                      // Show fields in order: Trim -> Cab Type -> Transmission -> Year
                      if (trims.length > 0) {
                          showTrimField(trims);
                      } else if (cabTypes.length > 0) {
                          showCabTypeField(cabTypes);
                      } else if (transmissions.length > 0) {
                          showTransmissionField(transmissions);
                      }

                  } catch (error) {
                      const secondaryFields = document.querySelector('.secondary-fields');
                      if (secondaryFields) secondaryFields.style.display = 'none';
                  }
              }
          });
      }

      // Add trim selection change handler
      if (trimSelect) {
          trimSelect.addEventListener('change', async function() {
              const selectedMake = makeSelect?.value;
              const selectedModel = modelSelect?.value;
              const selectedTrim = this.value;

              // Reset dependent fields
              const cabTypeSelect = document.getElementById('cabType');
              const transmissionSelect = document.getElementById('transmission');
              if (yearSelect) {
                  yearSelect.innerHTML = '<option value="">Select Year</option>';
                  yearSelect.disabled = true;
                  document.getElementById('year-field').style.display = 'none';
              }
              
              if (cabTypeSelect) {
                  cabTypeSelect.innerHTML = '<option value="">Select Cab Type</option>';
                  cabTypeSelect.disabled = true;
                  document.getElementById('cab-type-field').style.display = 'none';
              }
              
              if (transmissionSelect) {
                  transmissionSelect.innerHTML = '<option value="">Select Transmission</option>';
                  transmissionSelect.disabled = true;
                  document.getElementById('transmission-field').style.display = 'none';
              }

              if (selectedMake && selectedModel && selectedTrim) {
                  try {
                       const filterToApply = {
                           AND: [
                               { shopId: { equals: window.currentShopId.toString() } },
                               { make: { equals: selectedMake } },
                               { model: { equals: selectedModel } },
                               { trim: { equals: selectedTrim } }
                           ]
                       };
                       
                       const fitments = await fetchAllProductVehicleFitments(api, {
                           filter: filterToApply,
                           select: {
                               cabType: true,
                               transmission: true,
                               yearFrom: true,
                               yearTo: true
                           }
                       });

                       const fitmentsArray = Array.isArray(fitments) ? fitments : (fitments?.data || fitments?.items || []);

                       // Process cab types and transmissions
                      const cabTypes = [...new Set(fitmentsArray.map(f => f.cabType))].filter(Boolean).sort();
                      const transmissions = [...new Set(fitmentsArray.map(f => f.transmission))].filter(Boolean).sort();

                      // If no additional fields needed, show years directly
                      if (cabTypes.length === 0 && transmissions.length === 0) {
                          processYearsFromFitments(fitmentsArray, yearSelect);
                          return;
                      }

                      // Show relevant fields
                      if (cabTypes.length > 0) {
                          showCabTypeField(cabTypes);
                      } else if (transmissions.length > 0) {
                          showTransmissionField(transmissions);
                      }

                  } catch (error) {
                      const secondaryFields = document.querySelector('.secondary-fields');
                      if (secondaryFields) {
                          secondaryFields.style.display = 'none';
                      }
                  }
              }
          });
      }

      // Add cab type change handler
      if (cabTypeSelect) {
          cabTypeSelect.addEventListener('change', async function() {
              const selectedMake = makeSelect?.value;
              const selectedModel = modelSelect?.value;
              const selectedCabType = this.value;

              // Reset dependent fields
              const transmissionSelect = document.getElementById('transmission');
              if (transmissionSelect) {
                  transmissionSelect.innerHTML = '<option value="">Select Transmission</option>';
                  transmissionSelect.disabled = true;
                  document.getElementById('transmission-field').style.display = 'none';
              }
              if (yearSelect) {
                  yearSelect.innerHTML = '<option value="">Select Year</option>';
                  yearSelect.disabled = true;
                  document.getElementById('year-field').style.display = 'none';
              }

              if (selectedMake && selectedModel && selectedCabType) {
                  try {
                       const filterToApply = {
                           AND: [
                               { shopId: { equals: window.currentShopId.toString() } },
                               { make: { equals: selectedMake } },
                               { model: { equals: selectedModel } },
                               { cabType: { equals: selectedCabType } }
                           ]
                       };
                       
                       const fitments = await fetchAllProductVehicleFitments(api, {
                           filter: filterToApply,
                           select: {
                               transmission: true,
                               yearFrom: true,
                               yearTo: true
                           }
                       });

                       const fitmentsArray = Array.isArray(fitments) ? fitments : (fitments?.data || fitments?.items || []);

                       // Process transmissions
                      const transmissions = [...new Set(fitmentsArray.map(f => f.transmission))].filter(Boolean).sort();

                      // If no transmissions, show years directly
                      if (transmissions.length === 0) {
                          processYearsFromFitments(fitmentsArray, yearSelect);
                          return;
                      }

                       // Show transmission field
                       showTransmissionField(transmissions);

                   } catch (error) {
                       const secondaryFields = document.querySelector('.secondary-fields');
                      if (secondaryFields) {
                          secondaryFields.style.display = 'none';
                      }
                  }
              }
          });
      }

      // Add transmission change handler
      if (transmissionSelect) {
          transmissionSelect.addEventListener('change', async function() {
              const selectedMake = makeSelect?.value;
              const selectedModel = modelSelect?.value;
              const selectedTrim = trimSelect?.value;
              const selectedTransmission = this.value;

              if (!yearSelect) return;
              yearSelect.innerHTML = '<option value="">Select Year</option>';
              yearSelect.disabled = true;
              document.getElementById('year-field').style.display = 'none';

              if (selectedMake && selectedModel && selectedTransmission) {
                  try {
                      const filter = {
                          AND: [
                              { shopId: { equals: window.currentShopId.toString() } },
                              { make: { equals: selectedMake } },
                              { model: { equals: selectedModel } },
                              { transmission: { equals: selectedTransmission } }
                          ]
                      };

                       if (selectedTrim) {
                           filter.AND.push({ trim: { equals: selectedTrim } });
                       }

                       const response = await fetchAllProductVehicleFitments(api, {
                           filter,
                           select: {
                               yearFrom: true,
                               yearTo: true
                           }
                       });

                       const fitmentsArray = Array.isArray(response) ? response : (response?.data || response?.items || []);

                       processYearsFromFitments(fitmentsArray, yearSelect);

                  } catch (error) {
                      const secondaryFields = document.querySelector('.secondary-fields');
                      if (secondaryFields) {
                          secondaryFields.style.display = 'none';
                      }
                  }
              }
          });
      }

      // Add year selection handler
      if (yearSelect) {
          yearSelect.addEventListener('change', async function() {
              const selectedMake = makeSelect?.value;
              const selectedModel = modelSelect?.value;
              const selectedTrim = trimSelect?.value;
              const selectedYear = this.value;
              const selectedCabType = cabTypeSelect?.value;

              if (selectedMake && selectedModel && selectedYear) {
                  try {
                      const filter = {
                          AND: [
                              { shopId: { equals: window.currentShopId.toString() } },
                              { make: { equals: selectedMake } },
                              { model: { equals: selectedModel } }
                          ]
                      };

                      if (selectedTrim) {
                          filter.AND.push({ trim: { equals: selectedTrim } });
                      }

                       if (selectedCabType) {
                           filter.AND.push({ cabType: { equals: selectedCabType } });
                       }

                       const fitments = await fetchAllProductVehicleFitments(api, {
                           filter,
                           select: {
                               tag: true,
                               collections: true,
                               customTags: true,
                               universalTags: true,
                               yearFrom: true,
                               yearTo: true,
                               make: true,
                               model: true,
                               trim: true
                           }
                       });

                       const fitmentsArray = Array.isArray(fitments) ? fitments : (fitments?.data || fitments?.items || []);

                       const matchingFitment = fitmentsArray.find(f =>
                          parseInt(selectedYear) >= parseInt(f.yearFrom) && 
                          parseInt(selectedYear) <= parseInt(f.yearTo)
                      );

                      if (matchingFitment) {
                          // Save vehicle to localStorage
                          const vehicleToSave = {
                              make: selectedMake,
                              model: selectedModel,
                              trim: selectedTrim || '',
                              year: selectedYear,
                              cabType: selectedCabType || '',
                              transmission: transmissionSelect?.value || null,
                              fuelType: null
                          };
                          
                          // Ensure fitment has make/model for navigation
                          let fitmentToStore = {
                              ...matchingFitment,
                              make: matchingFitment.make || selectedMake,
                              model: matchingFitment.model || selectedModel,
                              trim: matchingFitment.trim || selectedTrim || ''
                          };
                          fitmentToStore = await hydrateUniversalTagsIfMissing(api, fitmentToStore);
                          
                          try {
                              localStorage.setItem('selectedVehicle', JSON.stringify(vehicleToSave));
                              // Store the fitment for navigation
                              localStorage.setItem('selectedFitment', JSON.stringify(fitmentToStore));
                          } catch (error) {
                              // Silently fail if localStorage isn't available
                          }
                          
                          // Determine collection URL before navigation
                          const relativeCollectionUrl = window.determineCollectionUrl(fitmentToStore);
                          const collectionUrl = window.location.origin + relativeCollectionUrl;
                          
                          // Log the vehicle search session
                          await logVehicleSearch(api, 'manual', vehicleToSave, null, collectionUrl);

                          // Show success UI with save to garage option
                          showSuccessUI(vehicleToSave, fitmentToStore, 'manual');
                      } else {
                          showNoProductsMessage();
                      }
                  } catch (error) {
                      showNoProductsMessage();
                  }
              }
          });
      }
  }

  // Shared initialization function
  async function initializeVehicleSearch() {
      try {
          // Initialize API with better error checking
          if (!window.Gadget && !window.VehicleSearchGadget) {
              return;
          }

          let api = window.vehicleSearchApi || window.vehicleApi;
          
          // Setup dropdown event listeners
          await setupDropdownEventListeners();

          // Registration search initialization
          const regSearchSection = document.querySelector(".vehicle-search__reg-section");
          const lookupButton = document.getElementById('lookupButton');
          const regInput = document.getElementById('registration');
          const errorDiv = document.getElementById('reg-search-error');

          // Initialize Gadget API and fetch settings
          api = window.vehicleSearchApi || window.vehicleApi;
          
          if (!api?.UKVehicleAPISettings) {
              return;
          }

          const gadgetSettings = await api.UKVehicleAPISettings.findMany({
              first: 1,
              select: { 
                  isEnabled: true
              },
              filter: {
                  shopId: { equals: window.currentShopId.toString() }
              }
          });

          const settings = window.vehicleSearchSettings || {};
          
          // Block setting takes precedence - if explicitly set in block settings, use it
          // Otherwise fall back to Gadget API setting
          if (settings.hasOwnProperty('isRegSearchEnabled') && typeof settings.isRegSearchEnabled === 'boolean') {
              // Use block setting as-is (already set from liquid template)
          } else {
              // Fall back to Gadget API setting if block setting not provided
              settings.isRegSearchEnabled = gadgetSettings?.[0]?.isEnabled ?? false;
          }

          // Handle reg search section visibility
          // Note: If block setting is false, the section won't be in the DOM at all
          // This code handles dynamic changes or cases where the section exists
          const divider = document.querySelector('.vehicle-search__divider');
          if (regSearchSection) {
              if (settings.isRegSearchEnabled) {
                  regSearchSection.classList.add('visible');
                  regSearchSection.classList.add('reg-search-enabled');
                  regSearchSection.style.display = 'block';
                  if (divider) {
                      divider.style.display = 'flex';
                  }
              } else {
                  regSearchSection.classList.remove('visible');
                  regSearchSection.classList.remove('reg-search-enabled');
                  regSearchSection.style.display = 'none';
                  if (divider) {
                      divider.style.display = 'none';
                  }
              }
          } else if (!settings.isRegSearchEnabled && divider) {
              // If section doesn't exist and reg search is disabled, hide divider
              divider.style.display = 'none';
          }

          // Registration lookup functionality
          if (lookupButton && regInput) {
              lookupButton.addEventListener('click', async (e) => {
                  e.preventDefault();
                  lookupButton.disabled = true;
                  const originalButtonContent = lookupButton.innerHTML;
                  lookupButton.innerHTML = '<svg class="loading-spinner" viewBox="0 0 50 50"><circle cx="25" cy="25" r="20" fill="none" stroke="currentColor" stroke-width="5"></circle></svg>';
                  errorDiv.textContent = '';

                  try {
                      const registration = regInput.value.trim();
                      
                      if (!registration) {
                          errorDiv.textContent = 'Please enter a registration number';
                          return;
                      }

                      if (!gadgetSettings?.[0]?.isEnabled) {
                          throw new Error("Vehicle lookup service not configured");
                      }

                      const shopId = window.currentShopId?.toString();
                      if (!shopId) {
                          throw new Error("Shop context unavailable. Please refresh and try again.");
                      }

                      const proxyUrl = `/apps/vehicles2-api/vehicle-lookup?vrm=${encodeURIComponent(registration)}&shopId=${encodeURIComponent(shopId)}`;
                      const directUrl = `https://vehicles2.gadget.app/vehicle-lookup?vrm=${encodeURIComponent(registration)}&shopId=${encodeURIComponent(shopId)}`;

                      let response = await fetch(proxyUrl);
                      const proxyContentType = response.headers.get('content-type') || '';

                      if ((response.ok && !proxyContentType.includes('application/json')) || (!response.ok && response.status >= 500)) {
                          response = await fetch(directUrl);
                      }

                      if (!response.ok) {
                          const contentType = response.headers.get('content-type') || '';
                          const errorData = contentType.includes('application/json')
                            ? await response.json().catch(() => ({}))
                            : { message: await response.text().catch(() => '') };
                          if (response.status === 404) {
                              throw new Error("Registration not found. Please check the number and try again.");
                          } else if (response.status === 401 || response.status === 403) {
                              throw new Error("Vehicle lookup service not properly configured. Please contact support.");
                          }
                          throw new Error(errorData.message || "Vehicle not found");
                      }

                      const successContentType = response.headers.get('content-type') || '';
                      if (!successContentType.includes('application/json')) {
                          throw new Error("Vehicle lookup proxy returned an invalid response. Please contact support.");
                      }

                      const data = await response.json();
                  
                      // Check if we got valid data
                      if (!data || typeof data !== 'object') {
                          throw new Error("Invalid vehicle data received");
                      }
                      
                      const vehicleData = processVehicleData(data);
                  
                      // Validate that we got at least basic vehicle info
                      if (!vehicleData.make || !vehicleData.model) {
                          throw new Error("Incomplete vehicle data received");
                      }

                      // Check if API is available
                      if (!api || !api.ProductVehicleFitment) {
                          throw new Error("Vehicle search API not available");
                      }

                      // First try with just make and model
                      let fitmentResults;
                      try {
                          // Normalize make - handle common variations (BMW, Bmw, bmw all become bmw)
                          const normalizedMake = vehicleData.make.toLowerCase();
                          
                          // First try with the full parsed model (e.g., "4 Series Gran Coupe")
                          let filterToApply = {
                              AND: [
                                  { shopId: { equals: window.currentShopId.toString() } },
                                  { lowercaseMake: { equals: normalizedMake } },
                                  { lowercaseModel: { equals: vehicleData.model.toLowerCase() } }
                              ]
                          };
                          fitmentResults = await fetchAllProductVehicleFitments(api, {
                              filter: filterToApply,
                              select: {
                                  yearFrom: true,
                                  yearTo: true,
                                  tag: true,
                                  trim: true,
                                  cabType: true,
                                  lowercaseTrim: true,
                                  transmission: true,
                                  fuelType: true,
                                  collections: true,
                                  customTags: true,
                                  universalTags: true,
                                  make: true,
                                  model: true
                              }
                          });
                      
                          // If no results, try model name variations (hyphen vs space, etc.)
                          const fitmentsArray = Array.isArray(fitmentResults) ? fitmentResults : (fitmentResults?.data || fitmentResults?.items || []);
                          if (fitmentsArray.length === 0) {
                              // Try different model name variations
                              const modelVariations = [];
                              
                              // If model contains a hyphen, try with space instead (e.g., "A-class" -> "A Class")
                              if (vehicleData.model.includes('-')) {
                                  modelVariations.push(vehicleData.model.replace(/-/g, ' '));
                              }
                              
                              // If model contains a space, try with hyphen instead (e.g., "A Class" -> "A-class")
                              if (vehicleData.model.includes(' ')) {
                                  modelVariations.push(vehicleData.model.replace(/\s+/g, '-'));
                              }
                              
                              // If model contains a space, try with just the first word (e.g., "A Class" -> "A")
                              if (vehicleData.model.includes(' ')) {
                                  const firstWord = vehicleData.model.split(' ')[0];
                                  if (firstWord.length > 0) {
                                      modelVariations.push(firstWord);
                                  }
                              }
                              
                              // Try base model (first two words) if model has multiple words
                              if (vehicleData.model.includes(' ')) {
                                  const baseModel = vehicleData.model.split(' ').slice(0, 2).join(' ');
                                  if (baseModel !== vehicleData.model) {
                                      modelVariations.push(baseModel);
                                  }
                              }
                              
                              // Try each variation until we find results
                              for (const modelVariation of modelVariations) {
                                  filterToApply = {
                                      AND: [
                                          { shopId: { equals: window.currentShopId.toString() } },
                                          { lowercaseMake: { equals: vehicleData.make.toLowerCase() } },
                                          { lowercaseModel: { equals: modelVariation.toLowerCase() } }
                                      ]
                                  };
                                  fitmentResults = await fetchAllProductVehicleFitments(api, {
                                      filter: filterToApply,
                                      select: {
                                          yearFrom: true,
                                          yearTo: true,
                                          tag: true,
                                          trim: true,
                                          cabType: true,
                                          lowercaseTrim: true,
                                          transmission: true,
                                          fuelType: true,
                                          collections: true,
                                          customTags: true,
                                          universalTags: true,
                                          make: true,
                                          model: true
                                      }
                                  });
                              
                                  const variationFitments = Array.isArray(fitmentResults) ? fitmentResults : (fitmentResults?.data || fitmentResults?.items || []);
                                  if (variationFitments.length > 0) {
                                      break; // Found results, stop trying variations
                                  }
                              }
                          }
                      } catch (apiError) {
                          fitmentResults = [];
                      }

                      // Handle Gadget API response - it might be an array or an object with items property
                      const fitmentsArray = Array.isArray(fitmentResults) ? fitmentResults : (fitmentResults?.data || fitmentResults?.items || []);
              
                      const sortedFitments = [...fitmentsArray].sort((a, b) => (b.transmission ? 1 : 0) - (a.transmission ? 1 : 0));
                      // Find ALL matching fitments, not just the first
                      const allMatches = sortedFitments.filter(f => matchFitment(vehicleData, f));

                      // If we have multiple matches, pick the best one based on year range
                      let matchingFitment = null;
                      if (allMatches.length > 0) {
                          if (allMatches.length === 1) {
                              matchingFitment = allMatches[0];
                          } else {
                              // Multiple matches - prefer the one where the vehicle year is more centered in the range
                              // Calculate distance from center of year range
                              matchingFitment = allMatches.reduce((best, current) => {
                                  const currentCenter = (parseInt(current.yearFrom) + parseInt(current.yearTo)) / 2;
                                  const currentDistance = Math.abs(vehicleData.year - currentCenter);
                              
                                  const bestCenter = (parseInt(best.yearFrom) + parseInt(best.yearTo)) / 2;
                                  const bestDistance = Math.abs(vehicleData.year - bestCenter);
                              
                                  // Prefer the fitment where the year is closer to the center of the range
                                  return currentDistance < bestDistance ? current : best;
                              });
                          }
                      }

                      if (matchingFitment) {
                          const vehicleToSave = {
                              make: vehicleData.make,
                              model: vehicleData.model,
                              trim: vehicleData.trim,
                              year: vehicleData.year,
                              cabType: vehicleData.cabType,
                              transmission: vehicleData.transmission,
                              fuelType: vehicleData.fuelType,
                              power: vehicleData.power,
                              powerKw: vehicleData.powerKw,
                              drive: vehicleData.drive,
                              driveType: vehicleData.driveType,
                              drivingAxle: vehicleData.drivingAxle,
                              engineDescription: vehicleData.engineDescription,
                              numberOfCylinders: vehicleData.numberOfCylinders,
                              numberOfGears: vehicleData.numberOfGears,
                              engineCapacity: vehicleData.engineCapacity,
                              torqueNm: vehicleData.torqueNm,
                              torqueLbFt: vehicleData.torqueLbFt,
                              maxSpeedMph: vehicleData.maxSpeedMph,
                              maxSpeedKph: vehicleData.maxSpeedKph,
                              combinedMpg: vehicleData.combinedMpg,
                              co2: vehicleData.co2,
                              vin: vehicleData.vin,
                              engineNumber: vehicleData.engineNumber,
                              registration: registration.toUpperCase(),
                              vrm: vehicleData.vrm || registration.toUpperCase()
                          };
                          
                          // Ensure fitment has make/model for navigation
                          let fitmentToStore = {
                              ...matchingFitment,
                              make: matchingFitment.make || vehicleData.make,
                              model: matchingFitment.model || vehicleData.model,
                              trim: matchingFitment.trim || vehicleData.trim
                          };
                          fitmentToStore = await hydrateUniversalTagsIfMissing(api, fitmentToStore);
                          
                          try {
                              localStorage.setItem('selectedVehicle', JSON.stringify(vehicleToSave));
                              // Store the fitment for navigation
                              localStorage.setItem('selectedFitment', JSON.stringify(fitmentToStore));
                          } catch (error) {
                              // Silently fail
                          }
                          
                          // Determine collection URL before navigation
                          const relativeCollectionUrl = window.determineCollectionUrl(fitmentToStore);
                          const collectionUrl = window.location.origin + relativeCollectionUrl;
                          
                          // Log the vehicle search session
                          await logVehicleSearch(api, 'registration', vehicleToSave, registration.toUpperCase(), collectionUrl);
                      
                          // Show success UI with save to garage option
                          showSuccessUI(vehicleToSave, fitmentToStore, 'registration');
                      } else {
                          // Log the failed search before showing the popup with simplified vehicle data
                          const simplifiedVehicleData = {
                              make: vehicleData.make,
                              model: vehicleData.model,
                              year: vehicleData.year,
                              trim: vehicleData.trim,
                              transmission: vehicleData.transmission,
                              fuelType: vehicleData.fuelType,
                              engineDescription: vehicleData.engineDescription,
                              engineCapacity: vehicleData.engineCapacity,
                              cabType: vehicleData.cabType,
                              power: vehicleData.power,
                              numberOfCylinders: vehicleData.numberOfCylinders
                          };
                          await logFailedSearch(api, registration, simplifiedVehicleData, 'No matching products found');
                          showNoProductsMessage();
                      }

                  } catch (error) {
                      // Log the failed search with the error reason
                      await logFailedSearch(api, regInput.value.trim(), null, error.message || 'Failed to look up vehicle');
                      errorDiv.textContent = error.message || 'An error occurred';
                  } finally {
                      lookupButton.disabled = false;
                      lookupButton.innerHTML = originalButtonContent;
                  }
              });
          }

      } catch (error) {
          // Silently fail if main initialization fails
      }
  }

  let vehicleSearchInitStarted = false;
  function startVehicleSearchInit() {
      if (vehicleSearchInitStarted) return;
      vehicleSearchInitStarted = true;
      initializeVehicleSearch();
  }

  // Listen for the API ready event dispatched from the Liquid file
  document.addEventListener('vehicle-search-api-ready', () => {
      startVehicleSearchInit();
  });

  // Also initialize on DOMContentLoaded in case the API is already loaded
  document.addEventListener('DOMContentLoaded', async function() {
      // Check if API is already available
      if (window.vehicleSearchApi || window.vehicleApi) {
          startVehicleSearchInit();
      }
      // Otherwise wait for vehicle-search-api-ready event
  });

  // Immediate bootstrap for cases where this script loads after DOMContentLoaded/event dispatch.
  if (window.vehicleSearchApi || window.vehicleApi) {
      startVehicleSearchInit();
  } else {
      let bootstrapAttempts = 0;
      const maxBootstrapAttempts = 40; // ~20 seconds at 500ms
      const bootstrapTimer = setInterval(() => {
          bootstrapAttempts += 1;
          if (window.vehicleSearchApi || window.vehicleApi) {
              clearInterval(bootstrapTimer);
              startVehicleSearchInit();
              return;
          }

          if (bootstrapAttempts >= maxBootstrapAttempts) {
              clearInterval(bootstrapTimer);
          }
      }, 500);
  }

  // Handle pages loaded from browser cache (bfcache)
  window.addEventListener('pageshow', async function(event) {
      // Only run if the page was loaded from cache
      if (event.persisted) {
          // Check if API is available and re-initialize if needed
          if (window.vehicleSearchApi || window.vehicleApi) {
              await setupDropdownEventListeners();
          }
      }
  });